'use client'

import { useState, useEffect } from 'react'
import { Book, CheckCircle, Circle, RefreshCw, Trash2, Edit2, FileText, ChevronDown, ChevronUp, Sparkles, Loader2, Download, FileJson, ArrowRight, X, Upload } from 'lucide-react'
import { useSectionOutlineGenerator } from '../../../../hooks/useSectionOutlineGenerator'
import { Label } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'

interface ChapterSectionUIProps {
  chapterSections: any[]
  selectedChapterId: string
  setSelectedChapterId: (id: string) => void
  showChapterSetup: boolean
  setShowChapterSetup: (show: boolean) => void
  savedPlots: any[]
  getPlot: (id: string) => any
  createChapterSection: (params: any) => string
  getChapterSection: (id: string) => any
  getNextPendingSection: (id: string) => any
  getCurrentOrNextSection: (id: string) => any
  updateSectionStatus: (chapterId: string, sectionNumber: number, status: any, sceneId?: string) => void
  updateSectionNotes: (chapterId: string, sectionNumber: number, notes: string) => void
  updateSectionSummary: (chapterId: string, sectionNumber: number, summary: string) => void
  updateSectionTargetLength: (chapterId: string, sectionNumber: number, targetLength: number) => void
  updateChapterAvoidWords: (chapterId: string, avoidWords: string[]) => void
  getScene: (id: string) => any
  deleteChapterSection: (id: string) => void
  markChapterAsCompleted: (id: string) => void
  generateChapterSummary: (id: string) => Promise<void>
  resetChapterSummary: (id: string) => void
  getCompletedChapters: (workId?: string) => any[]
  getCumulativeSummary: (upToChapter: number, workId?: string) => string
  exportChapterAsText: (chapterId: string) => void
  exportChapterAsJSON: (chapterId: string) => void
  importChapterFromJSON: (jsonString: string) => boolean
  useProMode?: boolean
  onReferenceChange?: (content: string) => void
  initialReferenceContent?: string  // 親から渡される初期参考資料
}

export function ChapterSectionUI({
  chapterSections,
  selectedChapterId,
  setSelectedChapterId,
  showChapterSetup,
  setShowChapterSetup,
  savedPlots,
  getPlot,
  createChapterSection,
  getChapterSection,
  getNextPendingSection,
  getCurrentOrNextSection,
  updateSectionStatus,
  updateSectionNotes,
  updateSectionSummary,
  updateSectionTargetLength,
  updateChapterAvoidWords,
  getScene,
  deleteChapterSection,
  markChapterAsCompleted,
  generateChapterSummary,
  resetChapterSummary,
  getCompletedChapters,
  getCumulativeSummary,
  exportChapterAsText,
  exportChapterAsJSON,
  importChapterFromJSON,
  useProMode,
  onReferenceChange,
  initialReferenceContent
}: ChapterSectionUIProps) {
  const [chapterTitle, setChapterTitle] = useState('')
  const [chapterNumber, setChapterNumber] = useState(1)
  const [targetLength, setTargetLength] = useState(20000)
  const [sectionCount, setSectionCount] = useState(4)
  const [selectedPlotId, setSelectedPlotId] = useState('')
  const [selectedPlotChapter, setSelectedPlotChapter] = useState(0)
  const [editingNotes, setEditingNotes] = useState<{ chapterId: string; sectionNumber: number } | null>(null)
  const [noteText, setNoteText] = useState('')
  const [showSummary, setShowSummary] = useState<string | null>(null)
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false)
  const [generatingOutlineFor, setGeneratingOutlineFor] = useState<number | 'all' | null>(null)
  const [editingOutline, setEditingOutline] = useState<{ chapterId: string; sectionNumber: number } | null>(null)
  const [outlineText, setOutlineText] = useState('')
  const [editingTargetLength, setEditingTargetLength] = useState<{ chapterId: string; sectionNumber: number } | null>(null)
  const [targetLengthText, setTargetLengthText] = useState('')
  const [editingAvoidWords, setEditingAvoidWords] = useState(false)
  const [avoidWordsText, setAvoidWordsText] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [referenceContent, setReferenceContent] = useState('')
  const [editingReference, setEditingReference] = useState(false)
  const { generateOutline, generateAllOutlines, isGenerating: isGeneratingOutline } = useSectionOutlineGenerator()

  const currentChapter = selectedChapterId ? getChapterSection(selectedChapterId) : undefined
  const nextSection = currentChapter ? getCurrentOrNextSection(selectedChapterId) : undefined
  
  // 現在の作品のworkIdを取得
  const currentWorkId = currentChapter?.workId || currentChapter?.plotId
  const completedChapters = getCompletedChapters(currentWorkId)
  
  // 初期参考資料の設定
  useEffect(() => {
    if (initialReferenceContent && !referenceContent) {
      setReferenceContent(initialReferenceContent)
      // ファイル名は不明なので、汎用的な名前を設定
      setUploadedFileName('参考資料 (親コンポーネントから)')
    }
  }, [initialReferenceContent])
  
  // 現在の作品に関連する章のみをフィルタリング
  const relevantChapterSections = (() => {
    if (!chapterSections || chapterSections.length === 0) return []
    
    // 現在選択されている章から作品を特定
    if (currentChapter) {
      const workId = currentChapter.workId || currentChapter.plotId
      if (workId) {
        return chapterSections.filter(ch => 
          ch.workId === workId || ch.plotId === workId
        )
      }
    }
    
    // 最新の章から作品を特定
    const latestChapter = chapterSections
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
    
    if (latestChapter) {
      const workId = latestChapter.workId || latestChapter.plotId
      if (workId) {
        return chapterSections.filter(ch => 
          ch.workId === workId || ch.plotId === workId
        )
      }
    }
    
    // workIdもplotIdもない章は独立した章として表示
    return chapterSections.filter(ch => !ch.workId && !ch.plotId)
  })()
  
  // 章が完了したかチェック
  useEffect(() => {
    if (currentChapter && !nextSection) {
      const allCompleted = currentChapter.sections.every((s: any) => s.status === 'completed')
      if (allCompleted && !currentChapter.isCompleted) {
        markChapterAsCompleted(currentChapter.id)
      }
    }
  }, [currentChapter, nextSection])

  // セクションあらすじ生成
  const handleGenerateSectionOutline = async (sectionNumber: number) => {
    if (!currentChapter) return
    
    setGeneratingOutlineFor(sectionNumber)
    
    try {
      // プロット情報を取得
      let plotContext = ''
      if (currentChapter.plotId && currentChapter.plotChapterNumber) {
        const plot = getPlot(currentChapter.plotId)
        if (plot && plot.result.structure) {
          const plotChapter = plot.result.structure.find((ch: any) => ch.number === currentChapter.plotChapterNumber)
          if (plotChapter) {
            plotContext = `【現在の章】\n章タイトル: ${plotChapter.title}\n章の概要: ${plotChapter.summary}`
            
            // 章に予定されているシーンがあれば追加
            if (plotChapter.scenes && plotChapter.scenes.length > 0) {
              plotContext += `\n\n【予定されているシーン】\n`
              plotChapter.scenes.forEach((scene: any, index: number) => {
                plotContext += `${index + 1}. ${scene.description}\n`
              })
            }
            
            // 後続章の情報も追加（流れの整合性のため）
            const nextChapters = plot.result.structure.filter((ch: any) => ch.number > currentChapter.plotChapterNumber)
            if (nextChapters.length > 0) {
              plotContext += `\n\n【後続の章の展開（ネタバレ防止に注意）】\n`
              nextChapters.slice(0, 2).forEach((ch: any) => {
                plotContext += `第${ch.number}章: ${ch.title} - ${ch.summary.substring(0, 100)}...\n`
              })
            }
          }
        }
      }
      
      // 前のセクションのあらすじを収集
      const previousSummaries = currentChapter.sections
        .filter((s: any) => s.sectionNumber < sectionNumber && s.summary)
        .map((s: any) => ({
          sectionNumber: s.sectionNumber,
          summary: s.summary
        }))
      
      // 前のセクションの実際のテキストを取得
      let previousSectionText = ''
      if (sectionNumber > 1) {
        const prevSection = currentChapter.sections[sectionNumber - 2]
        if (prevSection?.sceneId) {
          const prevScene = getScene(prevSection.sceneId)
          if (prevScene && prevScene.result.fullText) {
            previousSectionText = prevScene.result.fullText
          }
        }
      }
      
      const summary = await generateOutline({
        chapterTitle: currentChapter.chapterTitle,
        chapterNumber: currentChapter.chapterNumber,
        sectionNumber,
        totalSections: currentChapter.sections.length,
        targetLength: currentChapter.sections[sectionNumber - 1].targetLength,
        plotContext,
        previousSectionSummaries: previousSummaries,
        previousSectionText,  // 前のセクションの実際のテキストを追加
        useProMode,
        uploadedContent: referenceContent  // アップロードされた参考資料を渡す
      })
      
      updateSectionSummary(currentChapter.id, sectionNumber, summary)
    } catch (error) {
      console.error('あらすじ生成エラー:', error)
    } finally {
      setGeneratingOutlineFor(null)
    }
  }
  
  // 全セクションのあらすじ生成
  const handleGenerateAllOutlines = async () => {
    if (!currentChapter) return
    
    setGeneratingOutlineFor('all')
    
    try {
      // プロット情報を取得
      let plotContext = ''
      if (currentChapter.plotId && currentChapter.plotChapterNumber) {
        const plot = getPlot(currentChapter.plotId)
        if (plot && plot.result.structure) {
          const plotChapter = plot.result.structure.find((ch: any) => ch.number === currentChapter.plotChapterNumber)
          if (plotChapter) {
            plotContext = `【現在の章】\n章タイトル: ${plotChapter.title}\n章の概要: ${plotChapter.summary}`
            
            // 章に予定されているシーンがあれば追加
            if (plotChapter.scenes && plotChapter.scenes.length > 0) {
              plotContext += `\n\n【予定されているシーン】\n`
              plotChapter.scenes.forEach((scene: any, index: number) => {
                plotContext += `${index + 1}. ${scene.description}\n`
              })
            }
            
            // 後続章の情報も追加（流れの整合性のため）
            const nextChapters = plot.result.structure.filter((ch: any) => ch.number > currentChapter.plotChapterNumber)
            if (nextChapters.length > 0) {
              plotContext += `\n\n【後続の章の展開（ネタバレ防止に注意）】\n`
              nextChapters.slice(0, 2).forEach((ch: any) => {
                plotContext += `第${ch.number}章: ${ch.title} - ${ch.summary.substring(0, 100)}...\n`
              })
            }
          }
        }
      }
      
      const sectionTargetLength = currentChapter.sections[0]?.targetLength || 5000
      
      const outlines = await generateAllOutlines(
        currentChapter.chapterTitle,
        currentChapter.chapterNumber,
        currentChapter.sections.length,
        sectionTargetLength,
        plotContext,
        undefined, // worldContext
        undefined, // genre
        undefined, // writingStyle
        useProMode,
        referenceContent // アップロードされた参考資料を渡す
      )
      
      // 各セクションのあらすじを更新
      outlines.forEach(({ sectionNumber, summary }) => {
        updateSectionSummary(currentChapter.id, sectionNumber, summary)
      })
    } catch (error) {
      console.error('全セクションあらすじ生成エラー:', error)
    } finally {
      setGeneratingOutlineFor(null)
    }
  }

  return (
    <div className="space-y-4">
      {/* 既存の章セクション一覧 */}
      {relevantChapterSections.length > 0 && !showChapterSetup && (
        <div className="space-y-2">
          <Label>既存の章から選択</Label>
          <div className="space-y-2">
            {relevantChapterSections.map(chapter => {
              const completedCount = chapter.sections.filter((s: any) => s.status === 'completed').length
              const progress = (completedCount / chapter.sections.length) * 100
              
              return (
                <div
                  key={chapter.id}
                  className={`w-full p-3 border rounded-lg text-left hover:bg-gray-50 cursor-pointer ${
                    selectedChapterId === chapter.id ? 'border-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedChapterId(chapter.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">
                        第{chapter.chapterNumber}章: {chapter.chapterTitle}
                      </p>
                      <p className="text-sm text-gray-600">
                        {completedCount}/{chapter.sections.length}セクション完了
                        {chapter.plotId && ' (プロット連携)'}
                        {chapter.isCompleted && ' ✓ 完了'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={progress} className="w-24 h-2" />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm('この章を削除しますか？')) {
                            deleteChapterSection(chapter.id)
                            if (selectedChapterId === chapter.id) {
                              setSelectedChapterId('')
                            }
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      
      {/* 新規章作成・インポートボタン */}
      {!showChapterSetup && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => setShowChapterSetup(true)}
          >
            <Book className="mr-2 h-4 w-4" />
            新しい章を作成
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => {
              const input = document.createElement('input')
              input.type = 'file'
              input.accept = '.json'
              input.onchange = async (e) => {
                const file = (e.target as HTMLInputElement).files?.[0]
                if (file) {
                  try {
                    const text = await file.text()
                    const success = importChapterFromJSON(text)
                    if (success) {
                      // インポート成功時、章リストを更新するため再レンダリング
                      // （Zustandの状態更新により自動的に再レンダリングされる）
                    }
                  } catch (error) {
                    console.error('ファイル読み込みエラー:', error)
                    alert('ファイルの読み込みに失敗しました')
                  }
                }
              }
              input.click()
            }}
          >
            <Upload className="mr-2 h-4 w-4" />
            章をインポート
          </Button>
        </div>
      )}
      
      {/* 章セットアップフォーム */}
      {showChapterSetup && (
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold">新しい章の設定</h4>
          
          {/* プロット連携（オプション） */}
          <div>
            <Label>プロット連携（オプション）</Label>
            <Select
              value={selectedPlotId}
              onValueChange={(value) => {
                setSelectedPlotId(value)
                const plot = getPlot(value)
                if (plot && plot.result.structure.length > 0) {
                  setChapterTitle(plot.result.structure[0].title)
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="プロットを選択（任意）" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">プロットを使用しない</SelectItem>
                {savedPlots.map(plot => (
                  <SelectItem key={plot.id} value={plot.id}>
                    {plot.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedPlotId && selectedPlotId !== 'none' && (
            <div>
              <Label>プロットの章を選択</Label>
              <Select
                value={selectedPlotChapter.toString()}
                onValueChange={(value) => {
                  const chapterNum = parseInt(value)
                  setSelectedPlotChapter(chapterNum)
                  const plot = getPlot(selectedPlotId)
                  const chapter = plot?.result.structure.find((ch: any) => ch.number === chapterNum)
                  if (chapter) {
                    setChapterTitle(chapter.title)
                    setChapterNumber(chapter.number)
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="章を選択" />
                </SelectTrigger>
                <SelectContent>
                  {getPlot(selectedPlotId)?.result.structure.map((chapter: any) => (
                    <SelectItem key={chapter.number} value={chapter.number.toString()}>
                      第{chapter.number}章: {chapter.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>章番号</Label>
              <Input
                type="number"
                value={chapterNumber}
                onChange={(e) => setChapterNumber(parseInt(e.target.value) || 1)}
                min={1}
              />
            </div>
            <div>
              <Label>章タイトル</Label>
              <Input
                value={chapterTitle}
                onChange={(e) => setChapterTitle(e.target.value)}
                placeholder="例：始まりの朝"
              />
            </div>
          </div>
          
          <div>
            <Label>目標文字数</Label>
            <Select
              value={targetLength.toString()}
              onValueChange={(value) => setTargetLength(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10000">10,000文字（短編）</SelectItem>
                <SelectItem value="20000">20,000文字（中編）</SelectItem>
                <SelectItem value="30000">30,000文字（長編）</SelectItem>
                <SelectItem value="50000">50,000文字（超長編）</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>セクション数</Label>
            <Select
              value={sectionCount.toString()}
              onValueChange={(value) => setSectionCount(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2セクション</SelectItem>
                <SelectItem value="3">3セクション</SelectItem>
                <SelectItem value="4">4セクション</SelectItem>
                <SelectItem value="5">5セクション</SelectItem>
                <SelectItem value="6">6セクション</SelectItem>
                <SelectItem value="8">8セクション</SelectItem>
                <SelectItem value="10">10セクション</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-600 mt-1">
              各セクション約{Math.ceil(targetLength / sectionCount).toLocaleString()}文字
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => {
                if (chapterTitle) {
                  // 現在の作品のworkIdを取得
                  let workIdToPass: string | undefined = undefined
                  if (currentChapter) {
                    workIdToPass = currentChapter.workId || currentChapter.plotId
                  } else if (relevantChapterSections.length > 0) {
                    const latestChapter = relevantChapterSections[relevantChapterSections.length - 1]
                    workIdToPass = latestChapter.workId || latestChapter.plotId
                  }
                  
                  const chapterId = createChapterSection({
                    chapterTitle,
                    chapterNumber,
                    targetLength,
                    sectionCount,
                    plotId: selectedPlotId !== 'none' ? selectedPlotId : undefined,
                    plotChapterNumber: selectedPlotChapter || undefined,
                    workId: workIdToPass  // workIdを引き継ぐ
                  })
                  setSelectedChapterId(chapterId)
                  setShowChapterSetup(false)
                  // フォームをリセット
                  setChapterTitle('')
                  setChapterNumber(1)
                  setSelectedPlotId('')
                  setSelectedPlotChapter(0)
                }
              }}
              disabled={!chapterTitle}
            >
              章を作成
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowChapterSetup(false)
                setChapterTitle('')
                setSelectedPlotId('')
              }}
            >
              キャンセル
            </Button>
          </div>
        </div>
      )}
      
      {/* 選択された章の詳細 */}
      {currentChapter && !showChapterSetup && (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">
              第{currentChapter.chapterNumber}章: {currentChapter.chapterTitle}
            </h4>
            {currentChapter.isCompleted && (
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                完了
              </Badge>
            )}
          </div>
          
          {/* 禁止ワード設定 */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            {editingAvoidWords ? (
              <div className="space-y-2">
                <Label htmlFor="avoidWords" className="text-sm font-semibold">
                  禁止ワード・表現（読点区切り）
                </Label>
                <Textarea
                  id="avoidWords"
                  value={avoidWordsText}
                  onChange={(e) => setAvoidWordsText(e.target.value)}
                  onBlur={() => {
                    // フォーカスが外れた時に自動保存（配列に変換）
                    const words = avoidWordsText
                      .split(/[,、，]/)  // 半角カンマ、全角読点、全角カンマに対応
                      .map(w => w.trim())
                      .filter(w => w.length > 0)
                    updateChapterAvoidWords(currentChapter.id, words)
                  }}
                  placeholder="例: 違和感, 不自然な, 特定の名前"
                  rows={2}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    onClick={() => {
                      // onBlurで自動保存されるため、ここでも同じ処理を実行
                      const words = avoidWordsText
                        .split(/[,、，]/)
                        .map(w => w.trim())
                        .filter(w => w.length > 0)
                      updateChapterAvoidWords(currentChapter.id, words)
                      setEditingAvoidWords(false)
                    }}
                  >
                    保存
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingAvoidWords(false)
                      setAvoidWordsText('')
                    }}
                  >
                    キャンセル
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="cursor-pointer hover:bg-gray-100 p-2 rounded"
                onClick={() => {
                  setEditingAvoidWords(true)
                  setAvoidWordsText(currentChapter.avoidWords?.join('、') || '')
                }}
              >
                <p className="text-sm font-semibold">禁止ワード・表現:</p>
                {currentChapter.avoidWords && currentChapter.avoidWords.length > 0 ? (
                  <p className="text-sm text-gray-600 mt-1">
                    {currentChapter.avoidWords.join('、')}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 mt-1">
                    クリックして設定
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* 参照ファイル設定 */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            {editingReference ? (
              <div className="space-y-2">
                <Label htmlFor="referenceFile" className="text-sm font-semibold">
                  参照用ファイル（.txt形式）
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => document.getElementById('reference-file-input')?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploadedFileName || 'ファイルを選択'}
                  </Button>
                  <input
                    id="reference-file-input"
                    type="file"
                    accept=".txt"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      
                      try {
                        const text = await file.text()
                        setUploadedFileName(file.name)
                        setReferenceContent(text)
                        
                        // 親コンポーネントに参照内容を通知
                        if (onReferenceChange) {
                          onReferenceChange(text)
                        }
                        
                        console.log('【参照ファイル読込】', {
                          fileName: file.name,
                          fileSize: file.size,
                          textLength: text.length,
                          preview: text.substring(0, 200)
                        })
                      } catch (error) {
                        console.error('ファイル読込エラー:', error)
                        alert('ファイルの読み込みに失敗しました')
                      }
                    }}
                  />
                </div>
                {uploadedFileName && (
                  <Alert className="mt-2">
                    <AlertDescription>
                      <p>参照ファイル: {uploadedFileName}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {referenceContent.length}文字
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    onClick={() => {
                      setEditingReference(false)
                    }}
                  >
                    設定完了
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setUploadedFileName('')
                      setReferenceContent('')
                      setEditingReference(false)
                      
                      // 親コンポーネントにクリアを通知
                      if (onReferenceChange) {
                        onReferenceChange('')
                      }
                    }}
                  >
                    クリア
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className="cursor-pointer hover:bg-gray-100 rounded p-1"
                onClick={() => setEditingReference(true)}
              >
                <p className="text-sm font-semibold">参照用ファイル:</p>
                {uploadedFileName ? (
                  <p className="text-sm text-gray-600 mt-1">
                    {uploadedFileName} ({referenceContent.length}文字)
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">
                    クリックしてファイルをアップロード
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* 前章までの累積要約表示 */}
          {currentChapter.previousChaptersSummary && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowSummary(showSummary === 'cumulative' ? null : 'cumulative')}
              >
                <h5 className="text-sm font-semibold text-yellow-800">
                  📦 前章までのあらすじ
                </h5>
                {showSummary === 'cumulative' ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
              {showSummary === 'cumulative' && (
                <div className="mt-2">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {currentChapter.previousChaptersSummary}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    ※ この情報は各セクション執筆時に参照されます
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* あらすじ生成ボタン */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleGenerateAllOutlines}
              disabled={generatingOutlineFor === 'all'}
              className="flex-1"
            >
              {generatingOutlineFor === 'all' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  生成中...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  全セクションのあらすじを生成
                </>
              )}
            </Button>
          </div>
          
          {/* セクション一覧 */}
          <div className="space-y-2">
            {currentChapter.sections.map((section: any) => {
              const scene = section.sceneId ? getScene(section.sceneId) : undefined
              const isNext = section.sectionNumber === nextSection?.sectionNumber
              const isEditingNote = editingNotes?.chapterId === currentChapter.id && 
                                   editingNotes?.sectionNumber === section.sectionNumber
              
              return (
                <div
                  key={section.sectionNumber}
                  data-section={section.sectionNumber}
                  className={`flex items-start gap-3 p-3 rounded ${
                    isNext ? 'bg-blue-50 border border-blue-300' : 'bg-gray-50'
                  }`}
                >
                  <div className="mt-1">
                    {section.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : section.status === 'revision' ? (
                      <RefreshCw className="h-5 w-5 text-orange-500" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        セクション{section.sectionNumber}
                      </span>
                      {isNext && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                          次に執筆
                        </span>
                      )}
                    </div>
                    {/* 目標文字数表示・編集 */}
                    {editingTargetLength?.sectionNumber === section.sectionNumber ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={targetLengthText}
                          onChange={(e) => setTargetLengthText(e.target.value)}
                          className="w-32 h-7 text-sm"
                          placeholder="目標文字数"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const newLength = parseInt(targetLengthText)
                            if (newLength > 0) {
                              updateSectionTargetLength(currentChapter.id, section.sectionNumber, newLength)
                            }
                            setEditingTargetLength(null)
                            setTargetLengthText('')
                          }}
                        >
                          <CheckCircle className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingTargetLength(null)
                            setTargetLengthText('')
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 cursor-pointer hover:bg-gray-100 inline-block px-1 rounded"
                         onClick={() => {
                           setEditingTargetLength({ chapterId: currentChapter.id, sectionNumber: section.sectionNumber })
                           setTargetLengthText(section.targetLength.toString())
                         }}
                         title="クリックして文字数を編集">
                        目標: {section.targetLength.toLocaleString()}文字
                        {section.actualLength && (
                          <span> / 実際: {section.actualLength.toLocaleString()}文字</span>
                        )}
                      </p>
                    )}
                    {scene && (
                      <p className="text-sm mt-1">{scene.result.title}</p>
                    )}
                    
                    {/* あらすじ表示・編集 */}
                    {section.summary && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold">あらすじ:</p>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleGenerateSectionOutline(section.sectionNumber)}
                              disabled={generatingOutlineFor === section.sectionNumber}
                              title="あらすじを再生成"
                            >
                              {generatingOutlineFor === section.sectionNumber ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (editingOutline?.sectionNumber === section.sectionNumber) {
                                  // 保存
                                  updateSectionSummary(currentChapter.id, section.sectionNumber, outlineText)
                                  setEditingOutline(null)
                                  setOutlineText('')
                                } else {
                                  // 編集開始
                                  setEditingOutline({ chapterId: currentChapter.id, sectionNumber: section.sectionNumber })
                                  setOutlineText(section.summary)
                                }
                              }}
                              title={editingOutline?.sectionNumber === section.sectionNumber ? "保存" : "編集"}
                            >
                              {editingOutline?.sectionNumber === section.sectionNumber ? (
                                <CheckCircle className="h-3 w-3" />
                              ) : (
                                <Edit2 className="h-3 w-3" />
                              )}
                            </Button>
                            {editingOutline?.sectionNumber === section.sectionNumber && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingOutline(null)
                                  setOutlineText('')
                                }}
                                title="キャンセル"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {editingOutline?.sectionNumber === section.sectionNumber ? (
                          <Textarea
                            value={outlineText}
                            onChange={(e) => setOutlineText(e.target.value)}
                            className="w-full min-h-[100px] text-xs"
                            placeholder="あらすじを入力..."
                          />
                        ) : (
                          <p className="text-gray-700">{section.summary}</p>
                        )}
                      </div>
                    )}
                    
                    {/* メモ表示・編集 */}
                    {isEditingNote ? (
                      <div className="mt-2 space-y-2">
                        <Textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="メモを入力"
                          rows={2}
                          className="text-xs"
                        />
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              updateSectionNotes(currentChapter.id, section.sectionNumber, noteText)
                              setEditingNotes(null)
                              setNoteText('')
                            }}
                          >
                            保存
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingNotes(null)
                              setNoteText('')
                            }}
                          >
                            キャンセル
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {section.notes && (
                          <p className="text-xs text-gray-500 mt-1">メモ: {section.notes}</p>
                        )}
                        <div className="flex gap-1 mt-2">
                          {!section.summary && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleGenerateSectionOutline(section.sectionNumber)}
                              disabled={generatingOutlineFor === section.sectionNumber}
                            >
                              {generatingOutlineFor === section.sectionNumber ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3 mr-1" />
                              )}
                              あらすじ生成
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingNotes({ chapterId: currentChapter.id, sectionNumber: section.sectionNumber })
                              setNoteText(section.notes || '')
                            }}
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            メモ
                          </Button>
                          {section.status === 'completed' && (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  // ステータスをrevisionに変更
                                  updateSectionStatus(currentChapter.id, section.sectionNumber, 'revision')
                                  // セクションノートに書き直し開始を記録（オプション）
                                  const notes = section.notes || ''
                                  const timestamp = new Date().toLocaleString('ja-JP')
                                  updateSectionNotes(currentChapter.id, section.sectionNumber, 
                                    `${notes}${notes ? '\n' : ''}[${timestamp}] 書き直し開始`)
                                }}
                              >
                                書き直す
                              </Button>
                              {/* 次のセクションへ進むボタン */}
                              {section.sectionNumber < currentChapter.sections.length && 
                                currentChapter.sections[section.sectionNumber]?.status === 'pending' && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="default"
                                  onClick={() => {
                                    // 次のセクションをin_progressに設定
                                    updateSectionStatus(currentChapter.id, section.sectionNumber + 1, 'in_progress')
                                    // フォーカスを次のセクションに移動（視覚的フィードバック）
                                    const nextSection = currentChapter.sections[section.sectionNumber]
                                    if (nextSection) {
                                      // スクロール位置を調整
                                      const element = document.querySelector(`[data-section="${section.sectionNumber + 1}"]`)
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                      }
                                    }
                                  }}
                                >
                                  次のセクションへ →
                                </Button>
                              )}
                            </>
                          )}
                          {/* revision（書き直し）ステータスの場合のボタン */}
                          {section.status === 'revision' && (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="default"
                                onClick={() => {
                                  // 執筆モードに戻す（in_progressに変更）
                                  // 重要: 既存のsceneIdとfullTextは保持される
                                  console.log('【書き直し→執筆再開】', {
                                    chapterId: currentChapter.id,
                                    sectionNumber: section.sectionNumber,
                                    既存sceneId: section.sceneId,
                                    fullTextあり: !!section.fullText
                                  })
                                  updateSectionStatus(currentChapter.id, section.sectionNumber, 'in_progress')
                                }}
                              >
                                執筆を再開
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  // 書き直しをキャンセルして完了状態に戻す
                                  updateSectionStatus(currentChapter.id, section.sectionNumber, 'completed')
                                }}
                              >
                                キャンセル
                              </Button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          
          {/* 次のセクションの設定 */}
          {nextSection && (
            <Alert>
              <AlertDescription>
                セクション{nextSection.sectionNumber}を執筆します。
                {nextSection.sectionNumber > 1 && '前のセクションの内容は自動的に引き継がれます。'}
              </AlertDescription>
            </Alert>
          )}
          
          {!nextSection && currentChapter.sections.every((s: any) => s.status === 'completed') && (
            <div className="space-y-3">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  すべてのセクションが完了しました！
                </AlertDescription>
              </Alert>
              
              {/* 要約生成ボタン */}
              {!currentChapter.chapterSummary && (
                <Button
                  type="button"
                  onClick={async () => {
                    setIsGeneratingSummary(true)
                    await generateChapterSummary(currentChapter.id)
                    setIsGeneratingSummary(false)
                  }}
                  disabled={isGeneratingSummary}
                  className="w-full"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {isGeneratingSummary ? '要約を生成中...' : '章の要約を生成'}
                </Button>
              )}
              
              {/* エクスポートボタン */}
              {currentChapter && currentChapter.sections.some((s: any) => s.status === 'completed') && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => exportChapterAsText(currentChapter.id)}
                    className="flex-1"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    テキスト出力
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => exportChapterAsJSON(currentChapter.id)}
                    className="flex-1"
                  >
                    <FileJson className="mr-2 h-4 w-4" />
                    JSON出力
                  </Button>
                </div>
              )}
              
              {/* 要約表示 */}
              {currentChapter.chapterSummary && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setShowSummary(showSummary === currentChapter.id ? null : currentChapter.id)}
                  >
                    <h5 className="font-semibold flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      章の要約
                    </h5>
                    {showSummary === currentChapter.id ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                  
                  {showSummary === currentChapter.id && (
                    <div className="mt-3 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium mb-1">詳細な要約:</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {currentChapter.chapterSummary.detailedSummary}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (confirm('章要約をリセットしますか？')) {
                              resetChapterSummary(currentChapter.id)
                            }
                          }}
                          title="章要約をリセット"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {currentChapter.chapterSummary.keyEvents?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-1">重要な出来事:</p>
                          <ul className="text-sm text-gray-700 list-disc list-inside">
                            {currentChapter.chapterSummary.keyEvents.map((event: string, i: number) => (
                              <li key={i}>{event}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {currentChapter.chapterSummary.unresolved?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-1">未解決の伏線:</p>
                          <ul className="text-sm text-gray-700 list-disc list-inside">
                            {currentChapter.chapterSummary.unresolved.map((item: string, i: number) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* 次章への引き継ぎ情報 */}
              {completedChapters.length > 0 && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h5 className="font-semibold mb-2">次章への引き継ぎ</h5>
                  <p className="text-sm text-gray-600">
                    この章の要約と、今までの全章の累積要約が次章に引き継がれます。
                  </p>
                  <div className="mt-2">
                    <Badge variant="secondary">
                      完了済み: {completedChapters.length}章
                    </Badge>
                  </div>
                  
                  {/* 次の章へボタン - 章要約が生成済みの場合のみ表示 */}
                  {currentChapter?.chapterSummary && (
                    <Button
                      type="button"
                      className="w-full mt-3"
                      onClick={() => {
                        // 現在の章の次の章番号を計算
                        const nextChapterNumber = currentChapter.chapterNumber + 1
                        
                        // 現在の章の最終セクションの本文を確認（デバッグ用）
                        const lastCompletedSection = currentChapter.sections
                          .filter(s => s.status === 'completed')
                          .sort((a, b) => b.sectionNumber - a.sectionNumber)[0]
                        
                        let lastSectionText = ''
                        if (lastCompletedSection) {
                          if (lastCompletedSection.fullText) {
                            lastSectionText = lastCompletedSection.fullText
                          } else if (lastCompletedSection.sceneId) {
                            const scene = getScene(lastCompletedSection.sceneId)
                            if (scene) {
                              lastSectionText = scene.result.fullText
                            }
                          }
                        }
                        
                        console.log('【次章への引き継ぎ】現在の章の最終セクション:', {
                          chapterNumber: currentChapter.chapterNumber,
                          lastCompletedSection: lastCompletedSection?.sectionNumber,
                          hasFullText: !!lastCompletedSection?.fullText,
                          hasSceneId: !!lastCompletedSection?.sceneId,
                          textLength: lastSectionText.length,
                          textPreview: lastSectionText.substring(0, 100)
                        })
                        
                        // 累積要約を取得（getCumulativeSummaryが現在の章も含む）
                        const workId = currentChapter.workId || currentChapter.plotId
                        const cumulativeSummary = getCumulativeSummary(nextChapterNumber, workId)
                        console.log('【次章への引き継ぎ】累積要約の長さ:', cumulativeSummary.length)
                        
                        // プロットから次の章のタイトルを取得
                        let nextChapterTitle = `第${nextChapterNumber}章`
                        let nextPlotChapterNumber = currentChapter.plotChapterNumber ? currentChapter.plotChapterNumber + 1 : undefined
                        
                        if (currentChapter.plotId && nextPlotChapterNumber) {
                          const plot = getPlot(currentChapter.plotId)
                          if (plot && plot.result.structure) {
                            const nextPlotChapter = plot.result.structure.find((ch: any) => ch.number === nextPlotChapterNumber)
                            if (nextPlotChapter) {
                              nextChapterTitle = nextPlotChapter.title || `第${nextChapterNumber}章`
                              console.log('【次章への引き継ぎ】プロットから章情報取得:', {
                                plotChapterNumber: nextPlotChapterNumber,
                                title: nextPlotChapter.title,
                                summary: nextPlotChapter.summary?.substring(0, 100)
                              })
                            } else {
                              console.log('【次章への引き継ぎ】プロットに次の章が見つかりません')
                            }
                          }
                        }
                        
                        // 新しい章を作成（現在の章と同じ設定を使用）
                        const newChapterId = createChapterSection({
                          chapterTitle: nextChapterTitle,
                          chapterNumber: nextChapterNumber,
                          targetLength: currentChapter.targetLength || 20000,  // 現在の章の設定を引き継ぎ
                          sectionCount: currentChapter.sections.length || 4,  // 現在の章のセクション数を引き継ぎ
                          plotId: currentChapter.plotId,  // プロットIDも引き継ぎ
                          plotChapterNumber: nextPlotChapterNumber,
                          workId: currentChapter.workId,  // workIDも確実に引き継ぐ
                          cumulativeSummary  // 累積要約を引き継ぎ
                        })
                        
                        // 新しい章を選択
                        setSelectedChapterId(newChapterId)
                        
                        // チャプターセットアップを閉じる
                        setShowChapterSetup(false)
                      }}
                    >
                      <ArrowRight className="mr-2 h-4 w-4" />
                      次の章へ（第{currentChapter.chapterNumber + 1}章）
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}